import type { DeltaSource, RatingCard, RatingDelta } from '#shared/schemas/rating';
import { type CardsView, STATS_KEY, useCardsView, useDeltasQuery } from './queries';

/**
 * Stats' client API (add-ratings.md "REGISTRY HOOKS"). The first three names are frozen by the
 * Foundation; the rest are additions other modules may use.
 *
 * - useRatingCard(playerId): one rating card (unrated until the list arrives).
 * - useRatingCards(): every card plus a lookup by id, one cached request for the whole app.
 * - useDeltas(source): rating deltas of a game or drill session, null while the server has not
 *   computed them yet ("Ratings update when you get signal."). It polls a few times meanwhile.
 * - usePlayerStats, useBadges, useRatingHistory: the numbers behind a profile.
 * - deltaLines(delta): the recap copy for a delta (tier move, OVR line, the top three attributes).
 * - stateLine(card, now): the line under a card (Provisional, Rusty, Combine pending).
 * - STATS_KEY: invalidate it after anything that changes games or drills.
 */

export { deltaLines, stateLine } from './format';
export { useBadges, useHistory as useRatingHistory, usePlayerStats } from './queries';
export { STATS_KEY };

/** One player's rating card. An unrated card until the list has arrived. */
export function useRatingCard(playerId: string): RatingCard {
  return useCardsView().get(playerId);
}

/** Every card, plus a lookup by player id. An unknown id gets an unrated card from `get`. */
export function useRatingCards(): CardsView {
  return useCardsView();
}

/** Rating deltas of a game or drill session; null while not computed. */
export function useDeltas(source: DeltaSource | null): RatingDelta[] | null {
  return useDeltasQuery(source);
}
